import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Search, ShieldCheck, LogIn, User as UserIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";

const FEATURES = [
  {
    icon: Search,
    title: "Domande mirate",
    desc: "Un breve percorso guidato in italiano su uso, budget e preferenze.",
  },
  {
    icon: Sparkles,
    title: "Suggerito su misura",
    desc: "Un motore di raccomandazione seleziona da 3 a 5 modelli in base alle tue esigenze.",
  },
  {
    icon: ShieldCheck,
    title: "Consigli motivati",
    desc: "Ogni scelta è spiegata: capisci il perché prima di decidere.",
  },
];

export default function Landing() {
  const { user, logout } = useAuth();
  return (
    <div className="relative min-h-screen" data-testid="landing-page">
      <div className="absolute inset-0 subtle-grid opacity-40 pointer-events-none" />

      <header className="relative max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="font-heading text-xl font-semibold tracking-tight" data-testid="brand-logo">
          auto<span className="text-[#0055FF]">.</span>advisor
        </div>
        <div className="flex items-center gap-5">
          {user ? (
            <>
              <Link to="/history" className="text-sm font-medium text-[#111] hover:text-[#0055FF] transition-colors" data-testid="landing-history-link">
                Le mie ricerche
              </Link>
              <Link to="/account" className="text-sm font-medium text-[#111] hover:text-[#0055FF] transition-colors" data-testid="landing-account-link">
                Account
              </Link>
              <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#666] hover:text-[#111]" data-testid="landing-logout-button">
                <UserIcon className="w-4 h-4" /> Esci
              </button>
            </>
          ) : (
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#111] hover:text-[#0055FF] transition-colors" data-testid="landing-login-link">
              <LogIn className="w-4 h-4" /> Accedi
            </Link>
          )}
          <Link
            to="/wizard"
            className="text-sm font-medium text-[#111] hover:text-[#0055FF] transition-colors"
            data-testid="header-start-link"
          >
            Inizia →
          </Link>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-6 pt-10 md:pt-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <span className="inline-flex items-center gap-2 text-xs font-medium tracking-wide uppercase text-[#0055FF] bg-[#E5EEFF] rounded-full px-3 py-1 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Consulente auto personalizzato
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              L'automobile{" "}
              <span className="text-[#0055FF]">giusta</span>
              <br />
              per la tua vita.
            </h1>
            <p className="mt-6 text-lg text-[#666666] max-w-xl leading-relaxed">
              Rispondi a poche domande. I nostri consulenti scelgono da 3 a 5 automobili adatte al tuo stile di vita, al tuo budget e alle tue esigenze reali — con una spiegazione chiara per ognuna.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/wizard"
                className="group inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#111] text-white hover:bg-[#333] transition-colors font-medium"
                data-testid="landing-cta-start"
              >
                Trova la mia auto
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <span className="text-sm text-[#666]">Gratis · 2 minuti · Nessuna registrazione</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#EAEAEA] bg-white">
              <img
                src="https://images.unsplash.com/photo-1612545667889-b061512d0dfa?w=1400&q=85"
                alt="Automobile minimalista"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-white via-white/70 to-transparent">
                <p className="font-heading text-lg font-medium">
                  Un consulente digitale. <br />
                  Zero venditori insistenti.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="features-grid">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                className="p-6 rounded-2xl bg-white border border-[#EAEAEA]"
                data-testid={`feature-card-${i}`}
              >
                <Icon className="w-6 h-6 text-[#0055FF]" />
                <h3 className="mt-4 font-heading text-xl font-medium">{f.title}</h3>
                <p className="mt-2 text-sm text-[#666] leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </main>

      <footer className="relative max-w-6xl mx-auto px-6 py-8 border-t border-[#EAEAEA] flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-[#666]">
        <span>© {new Date().getFullYear()} auto.advisor — Consulente auto personalizzato</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-[#111]" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/cookies" className="hover:text-[#111]" data-testid="footer-cookie-link">Cookie</Link>
          <span>Powered by auto.advisor</span>
        </div>
      </footer>
    </div>
  );
}
